/* eslint-disable array-callback-return */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Input, Link, CollapsibleHedge } from '@trp-rps-rps-shared-web/rps-shared-web-components';
import * as tokens from '@trp-rps-rps-shared-web/rps-shared-web-styles';
import styled, { css } from 'styled-components';
import InputTilePopoverCard from './InputTilePopoverCard';
import { inputTilesActions, modalActions, contributionActions } from '../../redux/root-action';
import { modals, analyticsTracking } from '../../utils/constants';
import { getDomain } from '../../utils/api';
import { keyDown, getPreciseValue } from '../../utils/helpers';

interface Props {
  hasError?: boolean;
}

const StyledLink = styled.a`
  text-decoration: none;
  &:hover,
  &:focus {
    text-decoration: none;
  }
`;

const TileHeader = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 20px;
  letter-spacing: -0.3px;
  line-height: 26px;
  color: ${tokens.ColorGrey900};
`;

const TileSubheader = styled.div`
  font-family: Nimbus Sans Regular;
  font-size: 16px;
  line-height: 22px;
  color: ${tokens.ColorGrey700};
  letter-spacing: 0.4px;
  margin-bottom: 24px;
`;

const StyledText = styled.div<{ row?: number }>`
  font-family: Nimbus Sans Regular;
  font-size: 14px;
  line-height: 1.29;
  color: ${tokens.ColorGrey800};
  margin-top: 8px;
  -ms-grid-row: ${props => props.row};
  .bold {
    font-family: Nimbus Sans Bold;
  }
`;

const StyledPlanHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-top: 16px;
  :not(:first-of-type) {
    border-top: 1px solid ${tokens.ColorWhite100};
  }
  :target + div {
    display: grid;
  }
`;

const PlanNameAndAmount = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 22px;
  text-align: left;
  color: ${tokens.ColorGrey900};
`;

const PlanName = styled.div`
  font-family: Nimbus Sans Bold;
  @media all and (-ms-high-contrast: none) {
    width: calc(100% - 32px);
  }
`;

const PlanContributionAmount = styled.div`
  font-family: Nimbus Sans Regular;
`;

const ExpandCollapseIcon = styled.div`
  flex: 0;
`;

const StyledIcon = styled.img`
  width: 24px;
  height: 24px;
  margin: 0px 8px;
  cursor: pointer;
  @media all and (-ms-high-contrast: none) {
    margin: 0 0 0 -24px;
  }
  @media only screen and (max-width: 767px) {
    width: 32px;
    height: 32px;
  }
`;

const StyledInfoIcon = styled.img`
  margin: 0px;
  cursor: pointer;
  height: 12px;
  width: 12px;
  @media (max-width: 1024px) {
    display: none;
  }
`;

const StyledTableHeader = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 14px;
  line-height: 1.29;
  color: ${tokens.ColorGrey900};
`;

const StyledTableHeaderBlue = styled.div`
  font-family: Nimbus Sans Bold;
  font-size: 14px;
  line-height: 1.29;
  color: ${tokens.ColorBrandInteractiveBlue};
`;

const StyledBadge = styled.div<{ row?: number }>`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 75px;
  height: 40px;
  background-color: #e3f7ff;
  border-radius: 40px;
  font-family: Nimbus Sans Bold;
  font-size: 16px;
  line-height: 1.38;
  color: ${tokens.ColorGrey800};
  -ms-grid-row: ${props => props.row};
`;

const StyledInput = styled.div<{ row?: number }>`
  -ms-grid-row: ${props => props.row};
`;

const ContributionInfoContainer = styled.div`
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.5s ease-in-out;
  &.expanded {
    max-height: 1500px;
  }
`;

const StyledGrid = styled.div`
  display: -ms-grid;
  display: grid;
  grid-template-columns: 35% 1fr 1fr;
  column-gap: 16px;
  row-gap: 8px;
  .first-column {
    -ms-grid-column: 1;
  }
  .second-column {
    -ms-grid-column: 2;
  }
  .third-column {
    -ms-grid-column: 3;
  }
`;

const StyledTotalContributionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin: 16px 0 24px;
`;

const InputErrorMessage = styled.div`
  grid-column: 1 / span 3;
  font-family: Nimbus Sans Bold;
  color: ${tokens.ColorFunctionalRed700};
  font-size: 16px;
  line-height: 22px;
`;

const ErrorText = styled.span<Props>`
  ${(props: any) =>
    props.hasError &&
    css`
      color: ${tokens.ColorFunctionalRed700};
    `}
`;

const LegalText = styled.div`
  margin: 16px 0;
  border-top: 1px solid ${tokens.ColorWhite100};
  padding-top: 16px;
  font-size: 16px;
  font-style: italic;
  line-height: 22px;
  color: ${tokens.ColorGrey600};
`;

const FocusInput = styled.div`
  height: 0;
  width: 0;
  overflow: hidden;
  display: inline-block;
  > input {
    max-width: 0;
    max-height: 0;
  }
`;

class YourContribution extends Component<any> {
  props: any;
  state: any;
  constructor(props: any) {
    super(props);
    this.state = { isOpen: null };
  }

  handleValidateAndDone = () => {
    const {
      modelContributionData,
      hideInputTilePopover,
      contribution: {
        tempScenario: { contributions },
      },
    } = this.props;
    const contribErrorIndex = contributions.findIndex(
      (contrib: any) =>
        contrib.hasPlanLevelError ||
        contrib.hasPlanLevelErrorAmount ||
        contrib.contribution.filter((c: any) => c.hasError || c.hasErrorAmount)!.length > 0,
    );

    if (contribErrorIndex === -1) {
      modelContributionData();
      hideInputTilePopover();
    } else {
      this.setState({
        isOpen: contribErrorIndex,
      });
    }
  };

  handleCancel = () => {
    const {
      updateContributionData,
      hideInputTilePopover,
      contribution: { modelledScenario },
    } = this.props;
    updateContributionData(modelledScenario);
    hideInputTilePopover();
  };

  showContributionEducationModal = () => {
    const { showModal } = this.props;
    showModal(modals.CONTRIBUTION_EDUCATION);
  };

  showContributionSourceModal = () => {
    const { showModal } = this.props;
    showModal(modals.CONTRIBUTION_SOURCE_MODAL);
  };

  showContributionSourceModalOnKeyPress = (e: any) => {
    if (e.nativeEvent.code === 'Enter') {
      this.showContributionSourceModal();
    }
  };

  showContributionPerPaycheckModal = () => {
    const { showModal } = this.props;
    showModal(modals.CONTRIBUTION_PER_PAYCHECK_MODAL);
  };

  showContributionPerPaycheckModalOnKeyPress = (e: any) => {
    if (e.nativeEvent.code === 'Enter') {
      this.showContributionPerPaycheckModal();
    }
  };

  handleChange = (accountIndex: number, contributionIndex: number, contribution: any) => {
    return (event: any) => {
      const {
        updateContributionData,
        contribution: {
          tempScenario: { contributions },
          originalContributions,
        },
      } = this.props;

      const amount = Number(event.target.value);
      let totalContributionPercent = contributions[accountIndex].totalContributionPercent || 0;
      let totalContributionPercentCatchUp = contributions[accountIndex].totalContributionPercentCatchUp || 0;

      if (contribution.inputType !== 'B') {
        if (contribution.id !== '$' && contribution.id !== '#') {
          totalContributionPercent =
            (contributions[accountIndex].totalContributionPercent || 0) - (contribution.amount || 0) + (amount || 0);
        } else {
          totalContributionPercentCatchUp =
            (contributions[accountIndex].totalContributionPercentCatchUp || 0) -
            (contribution.amount || 0) +
            (amount || 0);
        }
      }

      const tempContribution = {
        ...contribution,
        amount,
        updated:
          originalContributions.contributions[accountIndex].contribution[contributionIndex].amount === amount
            ? false
            : true,
        hasError:
          contribution.maxAmountPercent > 0 && amount > getPreciseValue(contribution.maxAmountPercent * 100)
            ? true
            : false,
      };
      const tempContributions = [...contributions];
      tempContributions[accountIndex] = {
        ...tempContributions[accountIndex],
        totalContributionPercent,
        totalContributionPercentCatchUp,
        /* hasPlanLevelError:
          (totalContributionPercent > (tempContributions[accountIndex].planMaxAmountPercent * 100) || totalContributionPercentCatchUp > (tempContributions[accountIndex].planMaxCatchUpAmountPercent * 100) || (totalContributionPercent + totalContributionPercentCatchUp) > 100) ? true : false, */
        hasPlanLevelError:
          totalContributionPercent + totalContributionPercentCatchUp >
            (tempContributions[accountIndex].planMaxAmountPercent || 0) * 100
            ? true
            : false,
        contribution: tempContributions[accountIndex].contribution.map((c: any, i: number) => {
          if (i === contributionIndex) {
            return tempContribution;
          }
          return c;
        }),
      };

      /**
       * Checking user contribution input is <= of suggested contribution
       */
      if (
        originalContributions?.contributions[accountIndex]?.contribution[contributionIndex]?.suggestedBreakdown !==
        'N/A' &&
        parseInt(
          originalContributions.contributions[accountIndex].contribution[
            contributionIndex
          ].suggestedBreakdown.substring(1),
        ) <= amount
      ) {
        tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown = 'N/A';
      } else {
        tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown =
          originalContributions.contributions[accountIndex].contribution[contributionIndex].suggestedBreakdown;
      }

      let totalSuggestedBreakdown: any = 0;

      for (let i = 0; i < tempContributions[accountIndex].contribution.length; i++) {
        if (
          tempContributions[accountIndex].contribution[i].unit === 'PCT' &&
          tempContributions[accountIndex].contribution[i].suggestedBreakdown !== 'N/A'
        ) {
          totalSuggestedBreakdown =
            totalSuggestedBreakdown + parseInt(tempContributions[accountIndex].contribution[i].suggestedBreakdown);
        }
      }

      // tempContributions[accountIndex].totalSuggestedBreakdown = totalSuggestedBreakdown > 0 ? totalSuggestedBreakdown : 'N/A';

      tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown =
        originalContributions.contributions[accountIndex].contribution[contributionIndex].suggestedBreakdown;

      updateContributionData({ contributions: tempContributions });
    };
  };

  /**
   * For $ only
   * @param accountIndex
   * @param contributionIndex
   * @param contribution
   * @returns
   */
  handleChangeAmount = (accountIndex: number, contributionIndex: number, contribution: any) => {
    return (event: any) => {
      const {
        updateContributionData,
        contribution: {
          tempScenario: { contributions },
          originalContributions,
        },
      } = this.props;

      const amount = Number(event.target.value);
      let totalContributionAmount = contributions[accountIndex].totalContributionAmount || 0;
      let totalContributionAmountCatchUp = contributions[accountIndex].totalContributionAmountCatchUp || 0;

      if (contribution.inputType !== 'B') {
        if (contribution.id !== '$' && contribution.id !== '#') {
          totalContributionAmount =
            (contributions[accountIndex].totalContributionAmount || 0) - (contribution.amount || 0) + (amount || 0);
        } else {
          totalContributionAmountCatchUp =
            (contributions[accountIndex].totalContributionAmountCatchUp || 0) -
            (contribution.amount || 0) +
            (amount || 0);
        }
      }

      const tempContribution = {
        ...contribution,
        amount,
        updated:
          originalContributions.contributions[accountIndex].contribution[contributionIndex].amount === amount
            ? false
            : true,
        hasErrorAmount: contribution.maxAmountDollar > 0 && amount > contribution.maxAmountDollar ? true : false,
      };
      const tempContributions = [...contributions];
      tempContributions[accountIndex] = {
        ...tempContributions[accountIndex],
        totalContributionAmount,
        totalContributionAmountCatchUp,
        hasPlanLevelErrorAmount:
          totalContributionAmount > tempContributions[accountIndex].planMaxAmountDollar ||
            totalContributionAmountCatchUp > tempContributions[accountIndex].planMaxCatchUpAmountDollar
            ? true
            : false,
        contribution: tempContributions[accountIndex].contribution.map((c: any, i: number) => {
          if (i === contributionIndex) {
            return tempContribution;
          }
          return c;
        }),
      };

      /**
       * Checking user contribution input is <= of suggested contribution
       */
      if (
        originalContributions.contributions[accountIndex].contribution[contributionIndex].suggestedBreakdown !==
        'N/A' &&
        parseInt(
          originalContributions.contributions[accountIndex].contribution[
            contributionIndex
          ].suggestedBreakdown.substring(1),
        ) <= amount
      ) {
        tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown = 'N/A';
      } else {
        tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown =
          originalContributions.contributions[accountIndex].contribution[contributionIndex].suggestedBreakdown;
      }

      let totalSuggestedBreakdownAmount: any = 0;

      for (let i = 0; i < tempContributions[accountIndex].contribution.length; i++) {
        if (
          tempContributions[accountIndex].contribution[i].unit === 'DOL' &&
          tempContributions[accountIndex].contribution[i].suggestedBreakdown !== 'N/A'
        ) {
          totalSuggestedBreakdownAmount =
            totalSuggestedBreakdownAmount +
            parseInt(tempContributions[accountIndex].contribution[i].suggestedBreakdown.substring(1));
        }
      }

      // tempContributions[accountIndex].totalSuggestedBreakdownAmount = totalSuggestedBreakdownAmount > 0 ? totalSuggestedBreakdownAmount : 'N/A';
      tempContributions[accountIndex].contribution[contributionIndex].suggestedBreakdown =
        originalContributions.contributions[accountIndex].contribution[contributionIndex].suggestedBreakdown;

      updateContributionData({ contributions: tempContributions });
    };
  };

  calculateByPayFrequency = (payFrequency: string, savingAmount: number): number => {
    let annulizedAmout = 0;

    if (typeof payFrequency != 'undefined' && payFrequency && savingAmount > 0) {
      if (payFrequency === 'ANNL' || payFrequency === 'NTSP') {
        annulizedAmout = savingAmount;
      } else if (payFrequency === 'SANNL') {
        annulizedAmout = savingAmount / 2;
      } else if (payFrequency === 'QLY') {
        annulizedAmout = savingAmount / 4;
      } else if (payFrequency === 'MLY') {
        annulizedAmout = savingAmount / 12;
      } else if (payFrequency === 'TWMLY') {
        annulizedAmout = savingAmount / 24;
      } else if (payFrequency === 'BIWLY') {
        annulizedAmout = savingAmount / 26;
      } else if (payFrequency === 'WKLY') {
        annulizedAmout = savingAmount / 52;
      } else if (payFrequency === 'DLY') {
        annulizedAmout = savingAmount / 365;
      }
    }
    return annulizedAmout;
  };

  componentDidMount() {
    const {
      contribution: {
        originalContributions: { contributions },
        tempScenario,
      },
      simulationCurrentWant: { householdAccountAdvice },
      updateContributionData,
      compensations,
    } = this.props;

    if (!householdAccountAdvice) {
      return '';
    }

    contributions.forEach((account: any) => {
      const filteredAdvices = householdAccountAdvice.filter(
        (advice: any) => advice.AccountName.indexOf(account.planNumber) > -1,
      );
      account.totalSuggestedBreakdown = 0;
      account.totalSuggestedBreakdownAmount = 0;
      if (filteredAdvices.length && account.contribution) {
        account.contribution.forEach((contrib: any) => {
          const currentAdvice = filteredAdvices.filter(
            (advice: any) => advice.AccountName.lastIndexOf(contrib.id) === advice.AccountName.length - 1,
          );
          const hasAdvice = currentAdvice.length > 0;
          let suggestedBreakdown = 'N/A';
          if (hasAdvice && contrib.unit === 'PCT') {
            const savingsPercent = getPreciseValue(Number(currentAdvice[0].SavingsPercentageTotalSalary) * 100);
            const suggestedBreakdownAmount = savingsPercent > contrib.amount ? savingsPercent : 0;
            suggestedBreakdown = suggestedBreakdownAmount > 0 ? suggestedBreakdownAmount + '%' : 'N/A';
            account.totalSuggestedBreakdown = account.totalSuggestedBreakdown + suggestedBreakdownAmount;
          } else if (hasAdvice && contrib.unit === 'DOL') {
            let savingsAmount = 0;
            compensations.forEach((compen: any) => {
              if (compen.clientNumber === account.clientNumber) {
                const savingAmt = currentAdvice[0].SavingsAmount;
                savingsAmount = Math.trunc(Number(this.calculateByPayFrequency(compen.payFrequency, savingAmt)));
                return;
              }
            });

            const suggestedBreakdownAmount = savingsAmount > contrib.amount ? savingsAmount : 0;
            suggestedBreakdown = suggestedBreakdownAmount > 0 ? '$' + suggestedBreakdownAmount : 'N/A';
            account.totalSuggestedBreakdownAmount = account.totalSuggestedBreakdownAmount + suggestedBreakdownAmount;
          }
          contrib.suggestedBreakdown = suggestedBreakdown;
        });
      }
    });
    updateContributionData({ contributions: [...tempScenario.contributions] });
  }

  toggleAccordion = (id: any) => {
    const { isOpen } = this.state;
    this.setState({
      isOpen: isOpen === id ? null : id,
    });
  };

  render(): JSX.Element {
    const {
      contribution: {
        tempScenario: { contributions },
      },
    } = this.props;
    const { isOpen } = this.state;
    const isMultiplan = contributions.length > 1;

    return (
      <InputTilePopoverCard
        handleDone={this.handleValidateAndDone}
        handleCancel={this.handleCancel}
        analyticsId={analyticsTracking.RIXContributionsInputTileDone}
        cancelId={analyticsTracking.RIXContributionsInputTileCancel}
      >
        <FocusInput id={`contrib-focus-input`}>
          <input aria-label="RIX Contribution" autoFocus />
        </FocusInput>
        <TileHeader id={`contrib-tile-header`}>Your Contribution</TileHeader>
        <TileSubheader id={`contrib-tile-sub-header`}>
          Suggestions based on your plan{isMultiplan && 's'}.
          <CollapsibleHedge
            id={`contrib-collapse-hedge`}
            title={'How are modeling suggestions prioritized?'}
            bodyCopy={
              'Suggested contribution modeling increases default to pretax until you reach the IRS limit and then to after-tax, if available. If your plan offers Roth, you can model the impact of Roth contribution changes. <br/><br/>If multiple plans are available, the plan with the greatest employer match contribution is prioritized, then the plan with a lower match is utilized. When match is maximized in each plan, suggested contribution modeling increases are then prioritized based on the plan with the highest account balance.'
            }
          />
        </TileSubheader>
        {contributions.map((account: any, accountIndex: number) => {
          if (
            account.contribution &&
            account.contribution.length > 0 &&
            account.displayAccountCategory !== 'RPSNQAccountSummary' &&
            account.displayAccountCategory !== 'NonQualAccountSummary' &&
            account.displayAccountCategory !== 'RPSNQTRACAccountSummary'
          ) {
            const hasBonusSource = account.contribution.some((c: any) => c.inputType === 'B');
            const allUnits: any = [];

            account.contribution.some((c: any) => {
              allUnits.push(c.unit);
            });

            let sumOfCatchUP = 0;
            let sumOfNonCatchUP = 0;
            account.contribution.map((contribut: any, contributIndex: number) => {
              contribut.id === '$' || contribut.id === '#'
                ? (sumOfCatchUP += contribut.amount)
                : (sumOfNonCatchUP += contribut.amount);
            });

            return (
              <React.Fragment key={accountIndex}>
                {isMultiplan && (
                  <StyledPlanHeader id={`contrib-plan-header-` + accountIndex}>
                    <PlanNameAndAmount id={`contrib-plan-name-amount-` + accountIndex}>
                      <PlanName id={`contrib-plan-name-` + accountIndex}>{account.name}</PlanName>
                      {account.planLevelUnit !== 'N/A' && (
                        <PlanContributionAmount id={`contrib-plan-contrib-amount-` + accountIndex}>
                          Current contribution:{' '}
                          {account.planLevelUnit === 'PCT'
                            ? (account.totalContributionPercent || 0) +
                            (account.totalContributionPercentCatchUp || 0) +
                            '%'
                            : '$' +
                            ((account.totalContributionAmount || 0) + (account.totalContributionAmountCatchUp || 0))}
                        </PlanContributionAmount>
                      )}
                    </PlanNameAndAmount>
                    <ExpandCollapseIcon id={`contrib-expand-collapse-icon-` + accountIndex}>
                      <StyledIcon
                        id={'expand-collapse-icon-' + accountIndex}
                        src={`data:image/svg+xml;base64,${isOpen === accountIndex ? tokens.AssetIconMinusOutline : tokens.AssetIconPlusOutline
                          }`}
                        alt="plus icon"
                        onClick={() => this.toggleAccordion(accountIndex)}
                        tabIndex={0}
                        onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) =>
                          keyDown(e, () => this.toggleAccordion(accountIndex))
                        }
                      />
                    </ExpandCollapseIcon>
                  </StyledPlanHeader>
                )}

                <ContributionInfoContainer
                  id={`contrib-info-container-` + accountIndex}
                  className={isOpen === accountIndex || !isMultiplan ? 'expanded' : ''}
                >
                  <StyledGrid>
                    <StyledTableHeader id={`contrib-table-header-` + accountIndex} className="first-column">
                      Contribution&nbsp;
                      {hasBonusSource && (
                        <StyledLink
                          id={`contrib-source-modal-link-` + accountIndex}
                          onClick={this.showContributionSourceModal}
                          tabIndex={isOpen === accountIndex ? 0 : -1}
                          onKeyPress={this.showContributionSourceModalOnKeyPress}
                        >
                          <StyledInfoIcon
                            id={`contrib-savings-info-icon-` + accountIndex}
                            src={`data:image/svg+xml;base64,${tokens.AssetIconInfoOutline}`}
                            alt="Savings info icon"
                          />
                        </StyledLink>
                      )}
                      <br />
                      Source
                    </StyledTableHeader>
                    <StyledTableHeader
                      id={`contributions-to-consider-header-` + accountIndex}
                      className="second-column"
                    >
                      Contributions to Consider
                    </StyledTableHeader>
                    <StyledTableHeaderBlue id={`contribution-table-header-` + accountIndex} className="third-column">
                      Contribution&nbsp;
                      <StyledLink
                        id={`contrib-paycheck-modal-link-` + accountIndex}
                        onClick={this.showContributionPerPaycheckModal}
                        tabIndex={isOpen === accountIndex ? 0 : -1}
                        onKeyPress={this.showContributionPerPaycheckModalOnKeyPress}
                      >
                        <StyledInfoIcon
                          id={`savings-info-icon` + accountIndex}
                          src={`data:image/svg+xml;base64,${tokens.AssetIconInfoOutline}`}
                          alt="Savings info icon"
                        />
                      </StyledLink>
                      <br />
                      per Paycheck
                    </StyledTableHeaderBlue>
                  </StyledGrid>

                  {/* Only % */}
                  <StyledGrid id={`contributions-grid-` + accountIndex}>
                    {account.contribution &&
                      account.contribution.map((contribution: any, contributionIndex: number) => {
                        if (contribution.unit === 'PCT') {
                          return (
                            <React.Fragment key={contributionIndex}>
                              {contribution.inputType !== 'B' && (
                                <>
                                  <StyledText
                                    id={`contrib-source-name-` + contributionIndex}
                                    className="first-column"
                                    row={contributionIndex + 2}
                                  >
                                    {contribution.sourceName}
                                  </StyledText>
                                  <StyledBadge
                                    id={`contrib-suggested-breakdown-` + contributionIndex}
                                    className="second-column"
                                    row={contributionIndex + 2}
                                  >
                                    {contribution.suggestedBreakdown || 'N/A'}
                                  </StyledBadge>
                                  <StyledInput
                                    id={`contrib-styled-input-` + contributionIndex}
                                    className="third-column"
                                    row={contributionIndex + 2}
                                  >
                                    <Input
                                      name={'contributionPercent' + contributionIndex}
                                      value={contribution.amount || 0}
                                      placeholder=""
                                      onChange={this.handleChange(accountIndex, contributionIndex, contribution)}
                                      autoComplete="off"
                                      aria-label="contributionPercent"
                                      width={92}
                                      errorMessage={''}
                                      trailingCharacter={'%'}
                                      textAlign={'right'}
                                      hasError={contribution.hasError}
                                      tabIndex={isOpen === accountIndex ? 0 : -1}
                                      id={`contrib-input-` + contributionIndex}
                                    />
                                  </StyledInput>
                                  {contribution.hasError && (
                                    <InputErrorMessage id={`contrib-input-error-msg-` + contributionIndex}>
                                      {contribution.id === '$' || contribution.id === '#' ? 'Catch up' : 'Source'}{' '}
                                      contribution limit is {getPreciseValue(contribution.maxAmountPercent * 100) + '%'}
                                      .
                                    </InputErrorMessage>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          );
                        }
                      })}
                  </StyledGrid>
                  {allUnits.indexOf('PCT') > -1 ? (
                    <StyledTotalContributionContainer id={`contrib-styled-total-container` + accountIndex}>
                      <StyledText id={`total-contribution-text-` + accountIndex}>
                        <span className="bold">
                          Total Contribution:{' '}
                          <ErrorText hasError={account.hasPlanLevelError}>
                            {(account.totalContributionPercent || 0) + account.totalContributionPercentCatchUp}%
                          </ErrorText>
                        </span>
                      </StyledText>
                      <StyledText id={`total-contributions-to-consider-` + accountIndex}>
                        Total Contributions to Consider:{' '}
                        {account.totalSuggestedBreakdown > 0
                          ? Math.trunc(account.totalSuggestedBreakdown) + '%'
                          : 'N/A'}
                      </StyledText>
                    </StyledTotalContributionContainer>
                  ) : (
                    ''
                  )}
                  {account.hasPlanLevelError && (
                    <InputErrorMessage
                      id={`total-contribution-limit-error-` + accountIndex}
                      style={{ textAlign: 'right', marginBottom: '16px' }}
                    >
                      Total contribution limit is {(account.planMaxAmountPercent || 0) * 100}%.
                    </InputErrorMessage>
                  )}

                  {/* Only $ */}
                  <StyledGrid id={`contrib-dollar-grid-` + accountIndex}>
                    {account.contribution &&
                      account.contribution.map((contribution: any, contributionIndex: number) => {
                        if (contribution.unit === 'DOL') {
                          return (
                            <React.Fragment key={contributionIndex}>
                              {contribution.inputType !== 'B' && (
                                <>
                                  <StyledText
                                    id={'contrib-dollar-source-' + contributionIndex}
                                    className="first-column"
                                    row={contributionIndex + 2}
                                  >
                                    {contribution.sourceName}
                                  </StyledText>
                                  <StyledBadge
                                    id={'contrib-dollar-suggested-breakdown-' + contributionIndex}
                                    className="second-column"
                                    row={contributionIndex + 2}
                                  >
                                    {contribution.suggestedBreakdown || 'N/A'}
                                  </StyledBadge>
                                  <StyledInput
                                    id={'contrib-dollar-input-container-' + contributionIndex}
                                    className="third-column"
                                    row={contributionIndex + 2}
                                  >
                                    <Input
                                      id={'contrib-dollar-input-' + contributionIndex}
                                      name={'contributionAmount' + contributionIndex}
                                      value={contribution.amount || 0}
                                      placeholder=""
                                      onChange={this.handleChangeAmount(accountIndex, contributionIndex, contribution)}
                                      autoComplete="off"
                                      aria-label="contributionAmount"
                                      width={92}
                                      errorMessage={''}
                                      leadingCharacter={'$'}
                                      textAlign={'right'}
                                      hasError={contribution.hasErrorAmount}
                                      tabIndex={isOpen === accountIndex ? 0 : -1}
                                    />
                                  </StyledInput>
                                  {contribution.hasErrorAmount && (
                                    <InputErrorMessage id={'contrib-dollar-input-error-' + contributionIndex}>
                                      {contribution.id === '$' || contribution.id === '#' ? 'Catch up' : 'Source'}{' '}
                                      contribution limit is {contribution.maxAmountDollar}.
                                    </InputErrorMessage>
                                  )}
                                </>
                              )}
                            </React.Fragment>
                          );
                        }
                      })}
                  </StyledGrid>
                  {allUnits.indexOf('DOL') > -1 ? (
                    <StyledTotalContributionContainer id={'styled-total-contrib-dol-' + accountIndex}>
                      <StyledText id={`contrib-dollar-total-text-` + accountIndex}>
                        <span className="bold">
                          Total Contribution:{' '}
                          <ErrorText
                            id={`contrib-dollar-total-error-` + accountIndex}
                            hasError={account.hasPlanLevelErrorAmount}
                          >
                            ${(account.totalContributionAmount || 0) + (account.totalContributionAmountCatchUp || 0)}
                          </ErrorText>
                        </span>
                      </StyledText>
                      <StyledText id={`contrib-dollar-styled-text-` + accountIndex}>
                        Total Contributions to Consider:{' '}
                        {account.totalSuggestedBreakdownAmount > 0
                          ? '$' + Math.trunc(account.totalSuggestedBreakdownAmount)
                          : 'N/A'}
                      </StyledText>
                    </StyledTotalContributionContainer>
                  ) : (
                    ''
                  )}
                  {account.hasPlanLevelErrorAmount && (
                    <InputErrorMessage
                      id={`contrib-dollar-input-error-` + accountIndex}
                      style={{ textAlign: 'right', marginBottom: '16px' }}
                    >
                      Total contribution limit is $
                      {(account.totalContributionAmount ? account.planMaxAmountDollar : 0) +
                        (account.totalContributionAmountCatchUp ? account.planMaxCatchUpAmountDollar : 0)}
                      .
                    </InputErrorMessage>
                  )}

                  <Link
                    id={`contrib-education-modal-link-` + accountIndex}
                    linkLabel={'Why are we suggesting modeling these amounts?'}
                    onClick={this.showContributionEducationModal}
                    color={tokens.ColorBrandInteractiveBlue}
                    hoverColor={tokens.ColorBrandInteractiveBlueHover}
                    tabIndex={isOpen === accountIndex ? 0 : -1}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) =>
                      keyDown(e, this.showContributionEducationModal)
                    }
                  />
                  {account.onlineContribEligible ? (
                    <LegalText id={`contrib-legal-text-` + accountIndex}>
                      For modeling only. Changes will be reflected in your estimates and projections after you ‘Update
                      and See Impact’ on the main page.To implement them, you must&nbsp;
                      <Link
                        linkLabel={'change your actual contribution'}
                        href={
                          getDomain() +
                          '/rps/rws/account/participant/transactions/changecontributions.otc?accountKey=RPS_' +
                          account.planNumber
                        }
                        color={tokens.ColorBrandInteractiveBlue}
                        hoverColor={tokens.ColorBrandInteractiveBlueHover}
                        customStyles={'text-decoration: underline'}
                        tabIndex={isOpen === accountIndex ? 0 : -1}
                        id={analyticsTracking.RIXContributionsInputTileUpdateActualContribution}
                      />
                      .
                    </LegalText>
                  ) : (
                    <LegalText id={`contrib-legal-text-` + accountIndex}>
                      For modeling only. Changes will be reflected in your estimates and projections after you ‘Update
                      and See Impact’ on the main page. Contact your Plan Administrator to change your actual
                      contribution.
                    </LegalText>
                  )}
                </ContributionInfoContainer>
              </React.Fragment>
            );
          }
        })}
      </InputTilePopoverCard>
    );
  }
}

const mapStateToProps = (state: any) => {
  const {
    charts: { simulationCurrentWant },
    contribution,
    riScenario,
  } = state;

  return {
    simulationCurrentWant,
    contribution,
    accounts: riScenario.accounts,
    spouseRetirementStartAge: riScenario.spouseRetirementStartAge,
    compensations: riScenario.compensations,
  };
};

export default connect(mapStateToProps, {
  hideInputTilePopover: inputTilesActions.hideInputTilePopover,
  showModal: modalActions.showModal,
  updateContributionData: contributionActions.updateContributionData,
  modelContributionData: contributionActions.modelContributionData,
})(YourContribution);
